// Admin client for the Claude-Integrated Dev tracker (Phase 0). Same call pattern as the other
// admin APIs: the page acquires an EntraID token via useAuth().getApiToken() and passes it in.
import type { ElementContext } from "./bugPicker";

export type TrackerType =
  | "requirement"
  | "defect"
  | "enhancement"
  | "feedback"
  | "review-batch"
  | "work-order";
export type TrackerStatus =
  | "draft"
  | "new"
  | "triaged"
  | "designed"
  | "approved"
  | "in-progress"
  | "in-review"
  | "done"
  | "declined";

export const TRACKER_STATUSES: TrackerStatus[] = [
  "draft",
  "new",
  "triaged",
  "designed",
  "approved",
  "in-progress",
  "in-review",
  "done",
  "declined",
];

export interface TrackerItem {
  entityId: string;
  type: TrackerType;
  source: "admin" | "visitor" | "agent";
  status: TrackerStatus;
  title: string;
  body: string;
  context?: ElementContext;
  fingerprint?: string;
  regressionOf?: string;
  occurrences: { at: string; msg: string; contact?: string }[];
  /** True total (list is trimmed server-side; overflowed appends count here too). */
  occurrenceCount?: number;
  /** Review-batch items: the pinned annotations from one admin review session (summary form). */
  annotations?: { msg: string; context?: Partial<ElementContext> }[];
  annotationCount?: number;
  /** Work orders: the member items this order covers. Members point back via workOrder. */
  memberIds?: string[];
  workOrder?: string;
  history: { at: string; actor: string; note: string }[];
  createdAt: string;
  updatedAt: string;
}

const API = (import.meta.env.VITE_CONTENT_API_URL ?? "").replace(/\/$/, "");

export async function fetchTracker(token: string): Promise<TrackerItem[] | null> {
  if (!API) return null;
  try {
    const res = await fetch(`${API}/admin/tracker`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { items?: TrackerItem[] };
    return data.items ?? [];
  } catch {
    return null;
  }
}

export async function createTrackerItem(
  token: string,
  input: {
    type: TrackerType;
    title: string;
    body: string;
    status?: TrackerStatus;
  },
): Promise<boolean> {
  if (!API) return false;
  try {
    const res = await fetch(`${API}/admin/tracker`, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify(input),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Bundle existing r/d/e items into one work order (server sets members' workOrder + triaged). */
export async function promoteToWorkOrder(
  token: string,
  input: { memberIds: string[]; title: string; body: string; status?: TrackerStatus },
): Promise<boolean> {
  if (!API) return false;
  try {
    const res = await fetch(`${API}/admin/tracker`, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify({ op: "promote", ...input }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function updateTrackerItem(
  token: string,
  id: string,
  patch: { status?: TrackerStatus; note?: string; title?: string; body?: string },
): Promise<boolean> {
  if (!API) return false;
  try {
    const res = await fetch(`${API}/admin/tracker/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify(patch),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export interface RedPin {
  type: "requirement" | "enhancement" | "defect";
  msg: string;
  context: ElementContext;
}

/** RED Mode: turn a point-and-click session into tracker items — bundled into one work order
 *  (`asWorkOrder`), or filed as individual r/d/e items. */
export async function submitRed(
  token: string,
  input: { asWorkOrder: boolean; title?: string; pins: RedPin[] },
): Promise<boolean> {
  if (!API) return false;
  try {
    const res = await fetch(`${API}/admin/tracker`, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify({ op: "red-submit", ...input }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
